import type { SsoProtocol } from '@libs/enums';
import type { ItemAttributes, ItemNames } from '@ps-smart-cp/ppfw-client';
import { Characters, ItemName, Locale, SingleValue, UploadFile } from '@ps-smart-cp/ppfw-client';

interface Schema {
  protocol: SsoProtocol;
  metadataXml: File | null;
  clientId: string;
  clientSecret: string;
  issuerUrl: string;
  idpProvider: string;
}

interface Record {
  protocol: string;
  metadataXml: string;
  clientId: string;
  clientSecret: string;
  issuerUrl: string;
  idpProvider: string;
}

const attributes = {
  metadataXml: UploadFile.new(true),
  clientId: Characters.preset.freeFormat({ maxLen: 128, required: true }),
  clientSecret: Characters.preset.freeFormat({ maxLen: 512, required: true }),
  issuerUrl: Characters.preset.freeFormat({ maxLen: 2048, required: true }),
  idpProvider: SingleValue.shortValue(true),
} satisfies ItemAttributes<Schema>;

const names = {
  metadataXml: ItemName.of([Locale.ja, 'Metadata XML'], [Locale.en, 'Metadata XML']),
  clientId: ItemName.of([Locale.ja, 'クライアントID'], [Locale.en, 'Client ID']),
  clientSecret: ItemName.of([Locale.ja, 'クライアントシークレット'], [Locale.en, 'Client Secret']),
  issuerUrl: ItemName.of([Locale.ja, '発行者URL'], [Locale.en, 'Issuer URL']),
  idpProvider: ItemName.of([Locale.ja, 'プロバイダ'], [Locale.en, 'Provider']),
} satisfies ItemNames<Schema>;

const titleNames = {
  ssoSetting: ItemName.of([Locale.ja, 'SSO設定'], [Locale.en, 'SSO Setting']),
  ssoDisableDialog: ItemName.of([Locale.ja, '無効化確認'], [Locale.en, 'Confirmation of disablement']),
  ssoEnableDialog: ItemName.of([Locale.ja, '有効化確認'], [Locale.en, 'Confirmation of enablement']),
  ssoRequiredDisableDialog: ItemName.of(
    [Locale.ja, 'SSO強制の無効化確認'],
    [Locale.en, 'Confirmation of forced SSO disablement'],
  ),
  ssoRequiredEnableDialog: ItemName.of(
    [Locale.ja, 'SSO強制の有効化確認'],
    [Locale.en, 'Confirmation of forced SSO enablement'],
  ),
};

const labelNames = {
  domainNotFound: ItemName.of(
    [Locale.ja, 'SSOの設定に必要な接続情報が見つかりません。管理者までお問い合わせください。'],
    [
      Locale.en,
      'The connection info required for SSO setup could not be found. Please contact your administrator.',
    ],
  ),
  idpReference: ItemName.of([Locale.ja, 'IdP設定値対応表'], [Locale.en, 'IdP Setting Reference']),
  copyTooltip: ItemName.of([Locale.ja, 'コピー'], [Locale.en, 'Copy']),
  oidcUnset: ItemName.of([Locale.ja, 'OIDC SSOは未設定です。'], [Locale.en, 'OIDC SSO is not configured.']),
  oidcEnable: ItemName.of(
    [Locale.ja, 'OIDC SSOは有効です。無効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'OIDC SSO is enabled. To disable, click the checkbox.'],
  ),
  oidcDisable: ItemName.of(
    [Locale.ja, 'OIDC SSOは無効です。有効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'OIDC SSO is disabled. To enable, click the checkbox.'],
  ),
  samlUnset: ItemName.of([Locale.ja, 'SAML SSOは未設定です。'], [Locale.en, 'SAML SSO is not configured.']),
  samlEnable: ItemName.of(
    [Locale.ja, 'SAML SSOは有効です。無効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'SAML SSO is enabled. To disable, click the checkbox.'],
  ),
  samlDisable: ItemName.of(
    [Locale.ja, 'SAML SSOは無効です。有効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'SAML SSO is disabled. To enable, click the checkbox.'],
  ),
  ssoRequired: ItemName.of([Locale.ja, 'ログインユーザにSSOを強制する'], [Locale.en, 'Force SSO']),
  confirmDisableSso: ItemName.of(
    [Locale.ja, '無効化します。よろしいですか？'],
    [Locale.en, 'Disable. May I?'],
  ),
  confirmEnableSso: ItemName.of([Locale.ja, '有効化します。よろしいですか？'], [Locale.en, 'Enable. May I?']),
  confirmDisableSsoRequired: ItemName.of(
    [Locale.ja, 'SSO強制を無効化します。よろしいですか？'],
    [Locale.en, 'Disable forced SSO. May I?'],
  ),
  confirmEnableSsoRequired: ItemName.of(
    [Locale.ja, 'SSO強制を有効化します。よろしいですか？'],
    [Locale.en, 'Enable forced SSO. May I?'],
  ),
};

const ssoSetting = {
  attributes,
  names,
  labelNames,
  titleNames,
};

interface SsoSetting {
  schema: Schema;
  record: Record;
}

export type { SsoSetting };
export { ssoSetting };
